import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { globalSearch, type GlobalSearchResults, type Permission } from "../lib/api";
import { canSeeNav, DASHBOARD, NAV_SECTIONS } from "../lib/navigation";
import { useRights } from "../lib/useRights";

/**
 * Palette de commandes globale (Sprint X-2).
 *
 * Activee par Cmd+K (macOS) / Ctrl+K (Windows/Linux). Cherche dans POI,
 * Collections, Expositions, Parcours, Médias (filename) et propose des
 * raccourcis vers les pages du back-office.
 *
 * UX :
 * - Debounce 200ms sur la query (limite spam serveur)
 * - Navigation clavier : ↑/↓ + Enter, Esc ferme
 * - Click sur un resultat = navigate + close
 */

type Item =
  | { type: "poi"; id: string; label: string; sub: string }
  | { type: "collection"; id: string; slug: string; label: string }
  | { type: "exhibition"; id: string; slug: string; label: string; sub: string | null }
  | { type: "parcours"; id: string; slug: string; label: string; sub: string }
  | { type: "media"; id: string; label: string; sub: string }
  | { type: "shortcut"; href: string; label: string };

type Shortcut = { href: string; label: string; keywords: string };

/** Raccourcis = les entrées du menu que le compte voit (lib/navigation.ts),
 *  avec la section en préfixe : « Reporting › Billetterie » ≠ « Billetterie et
 *  ventes › Partenaires ». */
function shortcutsFor(perms: ReadonlySet<Permission>): Shortcut[] {
  return [
    { href: DASHBOARD.to, label: DASHBOARD.label, keywords: DASHBOARD.keywords ?? "" },
    ...NAV_SECTIONS.flatMap((section) =>
      section.items
        .filter((item) => canSeeNav(item, perms))
        .map((item) => ({
          href: item.to,
          label: `${section.title} › ${item.label}`,
          keywords: item.keywords ?? "",
        })),
    ),
  ];
}

function flattenResults(q: string, r: GlobalSearchResults | undefined, shortcuts: Shortcut[]): Item[] {
  const items: Item[] = [];
  if (r) {
    for (const p of r.pois) {
      items.push({
        type: "poi",
        id: p.id,
        label: p.title,
        sub: `${p.kind} · ${p.altoCode ?? p.audioguideCode ?? p.id}`,
      });
    }
    for (const c of r.collections) {
      items.push({ type: "collection", id: c.id, slug: c.slug, label: c.title });
    }
    for (const e of r.exhibitions) {
      items.push({
        type: "exhibition",
        id: e.id,
        slug: e.slug,
        label: e.title,
        sub: e.publicCode,
      });
    }
    for (const p of r.parcours) {
      items.push({
        type: "parcours",
        id: p.id,
        slug: p.slug,
        label: p.title,
        sub: p.profile,
      });
    }
    for (const m of r.medias) {
      items.push({ type: "media", id: m.id, label: m.filename, sub: m.kind });
    }
  }
  // Always include matching shortcuts at the bottom for navigation
  const qLower = q.toLowerCase();
  for (const sc of shortcuts) {
    if (
      !q ||
      sc.label.toLowerCase().includes(qLower) ||
      sc.keywords.toLowerCase().includes(qLower)
    ) {
      items.push({ type: "shortcut", href: sc.href, label: sc.label });
    }
  }
  return items;
}

function itemHref(item: Item): string {
  switch (item.type) {
    case "poi":
      return `/pois/${item.id}`;
    case "collection":
      // Sprint AX-2 : pas de page detail Collection — on cible la liste
      // avec un focus pour permettre a l'admin de retrouver l'item visuellement
      return `/collections?focus=${encodeURIComponent(item.id)}`;
    case "exhibition":
      return `/exhibitions?focus=${encodeURIComponent(item.id)}`;
    case "parcours":
      return `/parcours/${item.id}`;
    case "media":
      // Sprint AX-2 : ?openId=X demande a MediaPage d'ouvrir la modale Detail
      // pour ce media. Avant on retournait juste /media et l'admin devait
      // chercher manuellement parmi 100+ media.
      return `/media?openId=${encodeURIComponent(item.id)}`;
    case "shortcut":
      return item.href;
  }
}

function typeLabel(t: Item["type"]): string {
  return (
    { poi: "POI", collection: "Collection", exhibition: "Exposition", parcours: "Parcours", media: "Média", shortcut: "Raccourci" } as const
  )[t];
}

export function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Raccourci global Cmd+K / Ctrl+K. Le reset (query/debounced/activeIndex)
  // est fait INLINE au moment du toggle plutot que dans un useEffect dependant
  // de `open` — evite la regle react-hooks/set-state-in-effect (React 19).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((current) => {
          if (current) return false; // close
          // opening : reset state ici (callback est async par rapport au render
          // de l'effet, donc pas flagged par la lint)
          setQuery("");
          setDebounced("");
          setActiveIndex(0);
          window.setTimeout(() => inputRef.current?.focus(), 10);
          return true;
        });
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Debounce query → debounced (200ms)
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), 200);
    return () => window.clearTimeout(t);
  }, [query]);

  // V1.51 : la recherche porte sur les contenus ; sans le droit de les voir,
  // la palette ne propose que les raccourcis.
  const { perms, canReadContent } = useRights();
  const searchQuery = useQuery({
    queryKey: ["admin", "search", debounced],
    queryFn: () => globalSearch(debounced),
    enabled: open && debounced.length >= 2 && canReadContent,
    staleTime: 30_000,
  });

  const items = useMemo(
    () => flattenResults(debounced, canReadContent ? searchQuery.data : undefined, shortcutsFor(perms)),
    [debounced, searchQuery.data, perms, canReadContent],
  );

  // Sprint Y-CI fix : index clampe en derivation pure (pas d'useEffect avec
  // setState). Si la liste retrecit (debounce, scope change), on retombe sur
  // 0 sans cascade de re-renders.
  const safeIndex =
    items.length === 0 ? 0 : Math.min(activeIndex, items.length - 1);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(Math.min(safeIndex + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(Math.max(safeIndex - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = items[safeIndex];
      if (item) {
        navigate(itemHref(item));
        setOpen(false);
      }
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/30 backdrop-blur-sm flex items-start justify-center pt-[10vh]"
      onClick={() => setOpen(false)}
      role="dialog"
      aria-modal="true"
      aria-label="Recherche globale"
    >
      <div
        className="w-full max-w-2xl mx-4 bg-white rounded-xl shadow-xl border border-[color:var(--color-alto-border)] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-[color:var(--color-alto-border)] flex items-center gap-3">
          <span aria-hidden className="text-[color:var(--color-alto-muted)]">⌕</span>
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Rechercher POI, collection, parcours, média… ou taper une page (ex: import)"
            className="flex-1 outline-none text-sm bg-transparent"
          />
          <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-[color:var(--color-alto-bg)] border border-[color:var(--color-alto-border)] text-[color:var(--color-alto-muted)]">
            esc
          </span>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {debounced.length >= 2 && searchQuery.isFetching && (
            <p className="px-4 py-3 text-sm text-[color:var(--color-alto-muted)]">
              Recherche…
            </p>
          )}
          {items.length === 0 && !searchQuery.isFetching && (
            <p className="px-4 py-6 text-sm text-[color:var(--color-alto-muted)] text-center">
              {debounced.length < 2
                ? "Tapez au moins 2 caractères pour rechercher (ou utilisez les raccourcis ci-dessus)."
                : "Aucun résultat."}
            </p>
          )}
          <ul>
            {items.map((item, i) => {
              const active = i === safeIndex;
              return (
                <li key={`${item.type}-${i}`}>
                  <button
                    type="button"
                    onMouseEnter={() => setActiveIndex(i)}
                    onClick={() => {
                      navigate(itemHref(item));
                      setOpen(false);
                    }}
                    className={
                      "w-full text-left px-4 py-2.5 flex items-center gap-3 text-sm transition-colors " +
                      (active
                        ? "bg-[color:var(--color-alto-bg)]"
                        : "hover:bg-[color:var(--color-alto-bg)]")
                    }
                  >
                    <span className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded bg-white border border-[color:var(--color-alto-border)] text-[color:var(--color-alto-muted)] shrink-0">
                      {typeLabel(item.type)}
                    </span>
                    <span className="flex-1 min-w-0 truncate">{item.label}</span>
                    {"sub" in item && item.sub && (
                      <span className="text-xs text-[color:var(--color-alto-muted)] shrink-0 font-mono">
                        {item.sub}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
